import { useEffect, useState, useMemo, useRef } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PlantillaItemsEditor } from "@/components/PlantillaItemsEditor";
import { ModelosAgrupados } from "@/components/ModelosAgrupados";

const vacia = { nombre: "", categoria_id: "", modelo_ids: [], mano_de_obra: 0, frt: "", incluye: [], descripcion: "", palabras_clave: "", items: [], plantillas_padre_ids: [], es_maestra: false };

export const PlantillaFormDialog = ({ open, onOpenChange, plantilla, categorias, modelos, repuestos, onSaved, onRevisarHijas }) => {
  const [form, setForm] = useState(vacia);
  const [todasPlantillas, setTodasPlantillas] = useState([]);
  const [efectivo, setEfectivo] = useState(null);
  const originalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/plantillas").then(({ data }) => setTodasPlantillas(data)).catch(() => setTodasPlantillas([]));
    if (plantilla) {
      setForm(vacia);
      api.get(`/plantillas/${plantilla.id}`).then(({ data }) => {
        const items = data.items.map((it) => ({ repuesto_id: it.repuesto_id || null, prefijo: it.prefijo || null, nombre_referencia: it.nombre_referencia || null, codigo_parte: it.codigo_parte || null, nombre: it.nombre || null, cantidad: it.cantidad, opcional: !!it.opcional, fijo: !!it.fijo, cantidad_manual: it.cantidad_manual !== undefined ? !!it.cantidad_manual : true, cantidad_maxima: it.cantidad_maxima ?? null, cantidad_maxima_manual: !!it.cantidad_maxima_manual, grupo_palabra: it.grupo_palabra || null, grupo_bloques: it.grupo_bloques || [] }));
        setForm({ nombre: data.nombre, categoria_id: data.categoria_id || "",
          modelo_ids: data.modelo_ids || [], mano_de_obra: data.mano_de_obra || 0,
          frt: data.frt ?? "", incluye: data.incluye || [],
          descripcion: data.descripcion || "", palabras_clave: data.palabras_clave || "",
          plantillas_padre_ids: data.plantillas_padre_ids || [],
          es_maestra: !!data.es_maestra, items });
        originalRef.current = { mano_de_obra: data.mano_de_obra || 0, frt: data.frt ?? "",
          items, plantillas_padre_ids: data.plantillas_padre_ids || [] };
        const mo = data.mano_obra_efectiva || [];
        setEfectivo({ frt: Math.round(mo.reduce((a, m) => a + (Number(m.frt) || 0), 0) * 10) / 10,
                      mano: Math.round(mo.reduce((a, m) => a + (Number(m.monto) || 0), 0) * 100) / 100 });
      }).catch((e) => toast.error(errorMsg(e)));
    } else { setForm(vacia); originalRef.current = null; setEfectivo(null); }
  }, [open, plantilla]);

  // Origen de herencia/inclusión: si esta plantilla es maestra, solo puede heredar/incluir de otras maestras.
  const opcionesOrigen = useMemo(() =>
    todasPlantillas.filter((p) => p.id !== plantilla?.id && (!form.es_maestra || p.es_maestra)),
  [todasPlantillas, plantilla, form.es_maestra]);

  // Modelos heredados (informativo): unión recursiva de modelo_ids de las plantillas padre
  // seleccionadas, excluyendo los que la plantilla ya tiene como propios.
  const modelosHeredados = useMemo(() => {
    const plById = Object.fromEntries(todasPlantillas.map((p) => [p.id, p]));
    const acc = new Set();
    const visit = (id, seen) => {
      if (seen.has(id)) return;
      seen.add(id);
      const p = plById[id];
      if (!p) return;
      (p.modelo_ids || []).forEach((m) => acc.add(m));
      (p.plantillas_padre_ids || []).forEach((pid) => visit(pid, seen));
    };
    (form.plantillas_padre_ids || []).forEach((pid) => visit(pid, new Set()));
    (form.modelo_ids || []).forEach((m) => acc.delete(m));
    return [...acc];
  }, [todasPlantillas, form.plantillas_padre_ids, form.modelo_ids]);

  const modeloNombre = (id) => {
    const m = modelos.find((x) => x.id === id);
    return m ? (m.nombre_mostrar || `${m.marca} ${m.modelo}`) : id;
  };

  const guardar = async () => {
    const payload = { ...form, categoria_id: form.categoria_id || null, mano_de_obra: Number(form.mano_de_obra) || 0,
      frt: form.frt === "" || form.frt === null ? null : Number(form.frt), incluye: form.incluye || [] };
    try {
      if (!plantilla) {
        await api.post("/plantillas", payload);
        toast.success("Plantilla creada"); onOpenChange(false); onSaved(); return;
      }
      await api.patch(`/plantillas/${plantilla.id}`, payload);
      toast.success("Plantilla actualizada");
      // Guardado instantáneo: cerramos el diálogo de edición y refrescamos de inmediato.
      // El cálculo del preview de sincronización NO se dispara aquí (es lento); se ofrece
      // aparte con una confirmación liviana manejada por el padre.
      const hijas = todasPlantillas.filter((p) => p.plantilla_maestra_id === plantilla.id);
      onOpenChange(false); onSaved();
      if (form.es_maestra && hijas.length > 0 && onRevisarHijas) {
        const o = originalRef.current;
        const prefijosDe = (its) => new Set((its || []).filter((x) => !x.fijo && x.prefijo).map((x) => String(x.prefijo).toUpperCase()));
        const antes = prefijosDe(o?.items); const ahora = prefijosDe(form.items);
        const removidos = [...antes].filter((p) => !ahora.has(p));
        onRevisarHijas({ id: plantilla.id, nombre: form.nombre, prefijos_removidos: removidos, hijasCount: hijas.length });
      }
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const toggleModelo = (id) =>
    setForm((f) => ({ ...f, modelo_ids: f.modelo_ids.includes(id) ? f.modelo_ids.filter((m) => m !== id) : [...f.modelo_ids, id] }));

  const togglePadre = (id) =>
    setForm((f) => ({ ...f, plantillas_padre_ids: f.plantillas_padre_ids.includes(id)
        ? f.plantillas_padre_ids.filter((x) => x !== id) : [...f.plantillas_padre_ids, id] }));

  const agregarPrefijo = (rep) => {
    const pref = (rep.codigo_parte || "").split("-")[0].toUpperCase();
    if (!/^[A-Z0-9]{5}$/.test(pref)) { toast.error("Ese repuesto no tiene un prefijo válido (5 caracteres)"); return; }
    setForm((f) => f.items.some((it) => it.prefijo === pref)
      ? f : { ...f, items: [...f.items, { prefijo: pref, nombre_referencia: rep.nombre, repuesto_id: null, cantidad: 1, opcional: false, fijo: false, cantidad_manual: false }] });
  };

  const agregarPrefijoManual = (pref) => {
    setForm((f) => f.items.some((it) => it.prefijo === pref)
      ? f : { ...f, items: [...f.items, { prefijo: pref, nombre_referencia: `Prefijo ${pref}`, repuesto_id: null, cantidad: 1, opcional: false, fijo: false, cantidad_manual: false }] });
  };

  const agregarGrupoBloque = (palabraRaw, bloquesTexto) => {
    const palabra = (palabraRaw || "").trim().toUpperCase();
    const bloques = (bloquesTexto || "").split(",").map((b) => b.trim().toUpperCase()).filter(Boolean);
    if (!palabra || bloques.length === 0) { toast.error("Indique la pieza y al menos un bloque"); return; }
    setForm((f) => ({ ...f, items: [...f.items, { grupo_palabra: palabra, grupo_bloques: bloques,
      nombre_referencia: `Grupo: ${palabra}`, repuesto_id: null, prefijo: null, cantidad: 1,
      opcional: false, fijo: false, cantidad_manual: false }] }));
  };

  const agregarFijo = (rep) => {
    setForm((f) => f.items.some((it) => it.repuesto_id === rep.id && it.fijo)
      ? f : { ...f, items: [...f.items, { repuesto_id: rep.id, prefijo: null, nombre_referencia: null, cantidad: 1, opcional: false, fijo: true }] });
  };

  // Plantillas NO maestras: se agrega el repuesto puntual completo (con su repuesto_id).
  const agregarRepuesto = (rep) => {
    setForm((f) => f.items.some((it) => it.repuesto_id === rep.id && !it.fijo)
      ? f : { ...f, items: [...f.items, { repuesto_id: rep.id, prefijo: null, nombre_referencia: null, codigo_parte: rep.codigo_parte, nombre: rep.nombre, cantidad: 1, opcional: false, fijo: false }] });
  };

  const cambiarCantidad = (i, cantidad) =>
    setForm((f) => ({ ...f, items: f.items.map((x, xi) => (xi === i ? { ...x, cantidad } : x)) }));

  const toggleCantidadManual = (i) =>
    setForm((f) => ({ ...f, items: f.items.map((x, xi) => (xi === i ? { ...x, cantidad_manual: !x.cantidad_manual } : x)) }));

  const toggleOpcional = (i) =>
    setForm((f) => ({ ...f, items: f.items.map((x, xi) => (xi === i ? { ...x, opcional: !x.opcional } : x)) }));

  const cambiarCantidadMaxima = (i, cantidad_maxima) =>
    setForm((f) => ({ ...f, items: f.items.map((x, xi) => (xi === i
      ? { ...x, cantidad_maxima, cantidad_maxima_manual: true } : x)) }));

  const quitarItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, xi) => xi !== i) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader><DialogTitle>{plantilla ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle></DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto pr-1 -mr-1">
          <div className="space-y-1"><Label>Nombre del trabajo *</Label>
            <Input data-testid="plantilla-nombre-input" value={form.nombre} placeholder='Ej: "Cambio de pistón — Motor 150"'
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Categoría de trabajo</Label>
              <Select value={form.categoria_id || "ninguna"} onValueChange={(v) => setForm({ ...form, categoria_id: v === "ninguna" ? "" : v })}>
                <SelectTrigger data-testid="plantilla-categoria-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ninguna">Sin categoría</SelectItem>
                  {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>Mano de obra fija (₡, respaldo)</Label>
              <Input data-testid="plantilla-mano-obra-input" type="number" min="0" step="any" value={form.mano_de_obra}
                onChange={(e) => setForm({ ...form, mano_de_obra: e.target.value })} />
              {efectivo && form.plantillas_padre_ids?.length > 0 && efectivo.mano !== (Number(form.mano_de_obra) || 0) && (
                <p data-testid="plantilla-mano-obra-efectiva" className="text-[11px] text-teal-700">Efectivo con herencia: <b>₡{efectivo.mano.toLocaleString("es-CR")}</b> (propio + padres)</p>
              )}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>F.R.T. combinado (horas)</Label>
              <Input data-testid="plantilla-frt-input" type="number" min="0" step="0.1" value={form.frt}
                placeholder="Ej: 3.5" onChange={(e) => setForm({ ...form, frt: e.target.value })} />
              {efectivo && form.plantillas_padre_ids?.length > 0 && efectivo.frt !== (form.frt === "" ? 0 : Number(form.frt)) && (
                <p data-testid="plantilla-frt-efectivo" className="text-[11px] text-teal-700">FRT efectivo con herencia: <b>{efectivo.frt}</b> (propio {form.frt === "" ? 0 : Number(form.frt)} + padres)</p>
              )}
              <p className="text-[11px] text-muted-foreground">Tiempo total con desarme compartido. Si se define, se cobra tiempo × tarifa del grupo del vehículo; si no, se usa el monto fijo.</p></div>
            <div className="space-y-1"><Label>Incluye la mano de obra de{form.es_maestra ? " (solo maestras)" : ""}</Label>
              <div className="border border-border rounded-sm p-2 max-h-24 overflow-y-auto space-y-1">
                {opcionesOrigen.length === 0 &&
                  <p className="text-xs text-muted-foreground">No hay {form.es_maestra ? "otras maestras" : "plantillas"} para incluir.</p>}
                {opcionesOrigen.map((p) => (
                  <label key={p.id} title={p.nombre} className="flex items-start gap-2 text-xs cursor-pointer">
                    <Checkbox data-testid={`plantilla-incluye-check-${p.id}`} checked={form.incluye.includes(p.id)}
                      className="mt-0.5 shrink-0"
                      onCheckedChange={() => setForm({ ...form, incluye: form.incluye.includes(p.id) ? form.incluye.filter((x) => x !== p.id) : [...form.incluye, p.id] })} />
                    <span className="break-words leading-snug min-w-0">{p.nombre}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Si ambas operaciones están en la cotización, solo se cobra esta.</p></div>
          </div>
          <div className="space-y-1">
            <Label>Modelos compatibles</Label>
            <ModelosAgrupados modelos={modelos} seleccionados={form.modelo_ids} testPrefix="modelo"
              onChange={(ids) => setForm({ ...form, modelo_ids: ids })}
              emptyMsg='No hay modelos. Créalos desde el botón "Modelos".' />
            {modelosHeredados.length > 0 && (
              <div data-testid="plantilla-modelos-heredados" className="mt-1.5 rounded-sm border border-dashed border-teal-300 bg-teal-50/50 p-2">
                <p className="text-[11px] font-medium text-teal-700 mb-1">
                  + {modelosHeredados.length} modelo{modelosHeredados.length > 1 ? "s" : ""} heredado{modelosHeredados.length > 1 ? "s" : ""} de las plantillas padre (aplican también, no editable aquí):
                </p>
                <div className="flex flex-wrap gap-1">
                  {modelosHeredados.map((id) => (
                    <span key={id} data-testid={`plantilla-modelo-heredado-${id}`}
                      className="inline-block rounded-full bg-white border border-teal-200 px-2 py-0.5 text-[11px] text-teal-700">
                      {modeloNombre(id)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-1"><Label>Descripción</Label>
            <Input data-testid="plantilla-descripcion-input" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
          <label data-testid="plantilla-es-maestra" className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50/60 p-2.5 cursor-pointer">
            <Checkbox data-testid="plantilla-es-maestra-check" checked={!!form.es_maestra}
              onCheckedChange={(v) => setForm({ ...form, es_maestra: !!v })} />
            <span className="text-sm text-amber-800">
              Marcar como plantilla <b>maestra</b> (permite generar plantillas equivalentes para otros modelos con IA)
            </span>
          </label>
          <div className="space-y-1">
            <Label>Plantillas padre (hereda sus repuestos y mano de obra){form.es_maestra ? " — solo maestras" : ""}</Label>
            <div className="border border-border rounded-sm p-2 max-h-32 overflow-y-auto grid grid-cols-1 gap-1">
              {opcionesOrigen.length === 0 &&
                <p className="text-xs text-muted-foreground">No hay {form.es_maestra ? "otras maestras" : "otras plantillas"} para heredar.</p>}
              {opcionesOrigen.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={`plantilla-padre-check-${p.id}`}
                    checked={form.plantillas_padre_ids.includes(p.id)}
                    onCheckedChange={() => togglePadre(p.id)} />
                  {p.nombre}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Al cargar esta plantilla se agregarán también los repuestos y la mano de obra de las plantillas seleccionadas (recursivo, sin duplicar líneas ni sumar cantidades).</p>
          </div>
          <div className="space-y-1"><Label>Palabras clave (separadas por comas)</Label>
            <Input data-testid="plantilla-palabras-clave-input" value={form.palabras_clave}
              placeholder="Ej: medio motor, pistón, anillos, cilindro, empaques"
              onChange={(e) => setForm({ ...form, palabras_clave: e.target.value })} />
            <p className="text-xs text-muted-foreground">Sinónimos o sub-trabajos para que la plantilla aparezca en búsquedas relacionadas.</p></div>
          <PlantillaItemsEditor items={form.items} repuestos={repuestos} esMaestra={form.es_maestra}
            onAgregarPrefijo={agregarPrefijo} onAgregarFijo={agregarFijo} onAgregarPrefijoManual={agregarPrefijoManual}
            onAgregarGrupo={agregarGrupoBloque}
            onAgregarRepuesto={agregarRepuesto} onToggleCantidadManual={toggleCantidadManual}
            onCantidad={cambiarCantidad} onQuitar={quitarItem} onToggleOpcional={toggleOpcional}
            onCantidadMaxima={cambiarCantidadMaxima} />
        </div>
        <DialogFooter className="border-t border-slate-100 pt-3 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="guardar-plantilla-btn" onClick={guardar} disabled={!form.nombre}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
